// FeaturesDemoCall.js
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ListAltIcon from '@mui/icons-material/ListAlt';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Container, Grid, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import './FeaturesDemoCall.css';

const ACCENT = '#ef6a1d';
const NAVY = '#0b2447';
const NAVY_DEEP = '#061a36';

/* Try to parse a demo call string like
   "Demo call: Buy CDSL at 1225 Target 1265 Stoploss 1200"
   into structured pieces so we can render a real-looking signal card. */
const parseDemoCall = (raw = '') => {
  const text = String(raw).replace(/^demo call:?\s*/i, '').trim();
  const m = text.match(
    /(buy|sell)\s+([A-Z0-9.&-]+)\s+(?:at|@)\s+([\d.]+)[^0-9]+([\d.]+)[^0-9]+([\d.]+)/i
  );
  if (!m) return { raw: text };
  const [, side, symbol, entry, target, sl] = m;
  return {
    side: side.toUpperCase(),
    symbol: symbol.toUpperCase(),
    entry,
    target,
    sl,
  };
};

const FeaturesDemoCall = ({ features, demoCall }) => {
  const call = parseDemoCall(demoCall);
  const isBuy = call.side === 'BUY' || !call.side;
  const sideColor = isBuy ? '#22c55e' : '#ef4444';

  return (
    <Box sx={{ py: { xs: 5, md: 7 }, background: 'transparent' }}>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 3, md: 4 }}
          alignItems="stretch"
          justifyContent="center"
        >
        {/* Features card */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.55 }}
            style={{ height: '100%' }}
          >
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                background: '#fff',
                border: '1px solid rgba(11,36,71,0.08)',
                boxShadow: '0 20px 50px -20px rgba(11,36,71,0.18)',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.4,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1.6,
                    background:
                      'linear-gradient(135deg, rgba(239,106,29,0.15) 0%, rgba(239,106,29,0.04) 100%)',
                    color: ACCENT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(239,106,29,0.2)',
                  }}
                >
                  <ListAltIcon />
                </Box>
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    color: NAVY,
                    letterSpacing: -0.3,
                  }}
                >
                  Features
                </Box>
              </Box>

              <Stack
                divider={
                  <Box
                    sx={{ height: 1, background: 'rgba(11,36,71,0.06)' }}
                  />
                }
                spacing={0}
              >
                {features.map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.2,
                      py: 1.4,
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        fontSize: '1.05rem',
                        color: '#22c55e',
                        mt: 0.4,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: '0.78rem',
                          color: '#64748b',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: 0.2,
                        }}
                      >
                        {f.label}
                      </Box>
                      <Box
                        sx={{
                          fontSize: '0.98rem',
                          color: NAVY,
                          fontWeight: 600,
                        }}
                      >
                        {f.value}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </motion.div>
        </Grid>

        {/* Demo call card */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.55, delay: 0.1 }}
            style={{ height: '100%' }}
          >
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                minHeight: 380,
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                color: '#fff',
                background: `linear-gradient(160deg, ${NAVY_DEEP} 0%, ${NAVY} 50%, #123a73 100%)`,
                boxShadow: '0 30px 60px -20px rgba(11,36,71,0.4)',
                overflow: 'hidden',
                /* Grid pattern */
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                  backgroundSize: '40px 40px',
                  maskImage:
                    'radial-gradient(ellipse at top right, #000 30%, transparent 80%)',
                  WebkitMaskImage:
                    'radial-gradient(ellipse at top right, #000 30%, transparent 80%)',
                  pointerEvents: 'none',
                },
                /* Orange glow */
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -80,
                  right: -80,
                  width: 240,
                  height: 240,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(239,106,29,0.3) 0%, rgba(239,106,29,0) 70%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.4,
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.6,
                      background:
                        'linear-gradient(135deg, rgba(239,106,29,0.3) 0%, rgba(239,106,29,0.1) 100%)',
                      color: ACCENT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(239,106,29,0.35)',
                    }}
                  >
                    <NotificationsActiveIcon />
                  </Box>
                  <Box
                    component="h2"
                    sx={{
                      m: 0,
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      color: '#fff',
                      letterSpacing: -0.3,
                    }}
                  >
                    Demo Call
                  </Box>
                </Box>

                {/* Signal-style card */}
                {call.symbol ? (
                  <Box
                    sx={{
                      borderRadius: 3,
                      p: 2.5,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    {/* Top row: BUY/SELL chip + symbol + LIVE pill */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.4,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: 1.2,
                            background: `${sideColor}22`,
                            border: `1px solid ${sideColor}55`,
                            color: sideColor,
                            fontSize: '0.78rem',
                            fontWeight: 800,
                            letterSpacing: 0.8,
                          }}
                        >
                          <TrendingUpIcon sx={{ fontSize: '0.85rem' }} />
                          {call.side}
                        </Box>
                        <Box
                          sx={{
                            fontSize: '1.25rem',
                            fontWeight: 800,
                            color: '#fff',
                            letterSpacing: 0.5,
                          }}
                        >
                          {call.symbol}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.6,
                          fontSize: '0.7rem',
                          color: '#94a3b8',
                          fontWeight: 600,
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: '0.85rem' }} />
                        Sample
                      </Box>
                    </Box>

                    {/* Price rows */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ mb: 1 }}
                    >
                      {[
                        { label: 'Entry', value: call.entry, color: '#fff' },
                        { label: 'Target', value: call.target, color: '#22c55e' },
                        { label: 'Stop Loss', value: call.sl, color: '#ef4444' },
                      ].map((p) => (
                        <Box
                          key={p.label}
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            p: 1.5,
                            background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            textAlign: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              fontSize: '0.7rem',
                              color: '#94a3b8',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                              mb: 0.4,
                            }}
                          >
                            {p.label}
                          </Box>
                          <Box
                            sx={{
                              fontSize: '1.15rem',
                              fontWeight: 800,
                              color: p.color,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            ₹{p.value}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  /* Fallback: just show the raw text if we couldn't parse */
                  <Box
                    sx={{
                      borderRadius: 3,
                      p: 2.5,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      fontSize: '1rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {call.raw}
                  </Box>
                )}

              </Box>
            </Box>
          </motion.div>
        </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesDemoCall;
